import React, { useState } from 'react';

import { DEFAULT_DATE_FIELD_FORMAT, previewDateFieldFormat } from '@aline/common';
import { Autocomplete } from '@mui/material';
import { AlineAutocompleteInput } from 'src/components/AlineAutocompleteInput';
import { Apollo } from 'src/gql/generated';

interface DateFormatOption {
  label: string;
  value: Apollo.DateFieldFormat;
}

const DATE_FORMAT_OPTIONS: DateFormatOption[] = Object.values(Apollo.DateFieldFormat).map(
  value => ({
    label: previewDateFieldFormat(value),
    value,
  })
);

interface DateFormatSelectProps {
  onChange: (dateFormat: Apollo.DateFieldFormat) => Promise<void> | void;
  value: Apollo.DateFieldFormat | null | undefined;
  disabled?: boolean;
}

export const DateFormatSelect = React.memo(function DateFormatSelect({
  disabled,
  onChange,
  value,
}: DateFormatSelectProps): JSX.Element {
  /**
   * The input text is controlled so it always shows a saved format: MUI keeps
   * the clicked label otherwise, even when the pick saved nothing. The picked
   * format shows while its save is in flight, then the stored one takes over.
   */
  const [pendingFormat, setPendingFormat] = useState<Apollo.DateFieldFormat | null>(null);
  const [typedText, setTypedText] = useState<string | null>(null);
  const shownFormat = pendingFormat ?? value ?? DEFAULT_DATE_FIELD_FORMAT;
  const selectedOption =
    DATE_FORMAT_OPTIONS.find(option => option.value === shownFormat) ?? DATE_FORMAT_OPTIONS[0];

  return (
    <Autocomplete<DateFormatOption, false, true>
      disableClearable
      // A second pick before the first save lands would switch from a format not yet stored.
      disabled={disabled || pendingFormat !== null}
      getOptionKey={option => option.value}
      getOptionLabel={option => option.label}
      inputValue={typedText ?? selectedOption.label}
      isOptionEqualToValue={(option, selected) => option.value === selected.value}
      options={DATE_FORMAT_OPTIONS}
      renderInput={params => <AlineAutocompleteInput {...params} />}
      size="small"
      value={selectedOption}
      onChange={async (_event, selected) => {
        setTypedText(null);
        setPendingFormat(selected.value);
        try {
          await onChange(selected.value);
        } finally {
          setPendingFormat(null);
        }
      }}
      onClose={() => setTypedText(null)}
      onInputChange={(_event, text, reason) => {
        if (reason === 'input') setTypedText(text);
      }}
    />
  );
});
